import { LocationHierarchyEntity, LocationHierarchyLevelEntity } from '@smatchy/sdk';

/**
 * One address component of a Google geocoder or Place Details result. Written
 * out here, not taken from the Google typings, so this file needs neither the
 * Maps SDK nor its types: AddressComponent satisfies it,
 * which is what the mobile app and the pro web space both pass in.
 */
export interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

/*
 * The hierarchy of a place (country, region, department, city, arrondissement)
 * is built from what Google answers, on the client, and the API refuses an
 * activity whose hierarchy it cannot use. The mobile app and the pro web space
 * must build it the same way or a search by city finds one app's events and
 * not the other's: one copy, here (13 Oct 2026, moved out of the mobile app
 * when the pro event form needed it).
 */

function getComponent(components: AddressComponent[], type: string): AddressComponent | undefined {
  return components.find((part) => part.types.includes(type));
}

function toLevel(component?: AddressComponent): LocationHierarchyLevelEntity | undefined {
  if (!component?.long_name) {
    return undefined;
  }
  return {
    name: component.long_name,
    code: component.short_name,
  };
}

/**
 * The three French cities whose addresses carry an arrondissement, and the
 * postal-code base each one counts from. Only these have arrondissements, so
 * nothing else is ever given one.
 */
const FRENCH_ARRONDISSEMENT_CITIES: { city: string; base: number; max: number }[] = [
  { city: 'paris', base: 75000, max: 20 },
  { city: 'lyon', base: 69000, max: 9 },
  { city: 'marseille', base: 13000, max: 16 },
];

function normaliseCityName(name?: string): string {
  return (name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

function arrondissementOrdinal(nth: number): string {
  return nth === 1 ? '1er' : `${nth}e`;
}

/**
 * The arrondissement of a French address, or nothing.
 *
 * Read from the postal code first (75015 → "15e"), which is exact; a plain
 * "Paris" selection carries no single postal code, so it gets no arrondissement
 * rather than a false one. The sublocality name ("Paris 15e") is a fallback for
 * places that name the arrondissement without a postal component.
 */
function frenchArrondissementDistrict(
  cityName: string | undefined,
  postalCode: string | undefined,
  sublocalityName: string | undefined,
): LocationHierarchyLevelEntity | undefined {
  const city = normaliseCityName(cityName);
  const match = FRENCH_ARRONDISSEMENT_CITIES.find((candidate) => candidate.city === city);
  if (!match) {
    return undefined;
  }

  const postal = Number((postalCode || '').replace(/\s/g, ''));
  if (Number.isInteger(postal)) {
    const nth = postal - match.base;
    if (nth >= 1 && nth <= match.max) {
      return { name: arrondissementOrdinal(nth), code: String(postal) };
    }
  }

  const fromName = (sublocalityName || '').match(/(\d{1,2})\s*(?:er|è?me|e)\b/i);
  if (fromName) {
    const nth = Number(fromName[1]);
    if (nth >= 1 && nth <= match.max) {
      return { name: arrondissementOrdinal(nth), code: undefined };
    }
  }

  return undefined;
}

function namesEqual(left?: string, right?: string): boolean {
  if (!left || !right) {
    return false;
  }
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * Place Details often omits admin levels not used in postal formatting.
 * Reverse geocode by lat/lng fills missing region (not department — avoids
 * VN noise like Bà Nà leaking into Da Nang paths).
 */
export function needsHierarchyEnrichment(hierarchy?: LocationHierarchyEntity, selectedLevel?: string): boolean {
  if (!hierarchy) {
    return true;
  }
  if (selectedLevel === 'country') {
    return !hierarchy.country?.name;
  }
  if (selectedLevel === 'region') {
    return !hierarchy.region?.name;
  }
  if (selectedLevel === 'department') {
    return !hierarchy.department?.name && !hierarchy.region?.name;
  }
  // VN municipality: city == admin1, no separate region layer needed.
  if (hierarchy.city?.name && hierarchy.country?.code === 'VN') {
    return false;
  }
  // city / other: need region for FR-style "City, Region, Country"
  return !hierarchy.region?.name;
}

/**
 * What the API asks of a hierarchy before it will let an activity live there
 * (normalizeActivityLocation): a country, and a city for anything picked at
 * city level or finer, a department for a department.
 *
 * A hierarchy can be good enough to display and still be refused here. That is
 * exactly what happened to the places saved in the phone's history before the
 * API started asking for one: they looked fine in the list and the creation
 * came back 400.
 */
export function isActivityHierarchyComplete(hierarchy?: LocationHierarchyEntity, selectedLevel?: string): boolean {
  if (!hierarchy?.country?.name) {
    return false;
  }
  if (selectedLevel === 'department') {
    return !!hierarchy.department?.name;
  }
  return !!hierarchy.city?.name;
}

export function mergeLocationHierarchies(
  primary: LocationHierarchyEntity,
  secondary: LocationHierarchyEntity,
): LocationHierarchyEntity {
  return {
    city: primary.city || secondary.city,
    // Keep the selected place's arrondissement; never import one from the
    // reverse-geocode of a city centroid, which would invent a false "1er".
    district: primary.district,
    // Keep primary department; do not import reverse-geocode department noise.
    department: primary.department,
    region: primary.region || secondary.region,
    country: primary.country || secondary.country,
  };
}

/**
 * Build Option B hierarchy from Google Place Details address_components.
 * Parent placeIds are optional (components do not include them).
 */
export function buildLocationHierarchyFromAddressComponents(
  components: AddressComponent[] = [],
  selectedPlaceId?: string,
  selectedLevel?: string,
): LocationHierarchyEntity {
  const city = toLevel(getComponent(components, 'locality')) || toLevel(getComponent(components, 'postal_town'));
  // Do NOT use sublocality as city (VN wards like Hòa Cương) -- but DO read it,
  // with the postal code, to name a French arrondissement ("Paris 15e").
  const district = frenchArrondissementDistrict(
    city?.name,
    getComponent(components, 'postal_code')?.short_name,
    (getComponent(components, 'sublocality_level_1') || getComponent(components, 'sublocality'))?.long_name,
  );
  const department = toLevel(getComponent(components, 'administrative_area_level_2'));
  const region = toLevel(getComponent(components, 'administrative_area_level_1'));
  const countryComponent = getComponent(components, 'country');
  const country = countryComponent
    ? {
        name: countryComponent.long_name,
        code: countryComponent.short_name,
        placeId: selectedLevel === 'country' ? selectedPlaceId : undefined,
      }
    : undefined;

  const hierarchy: LocationHierarchyEntity = {
    city: city
      ? {
          ...city,
          placeId: selectedLevel === 'city' ? selectedPlaceId : city.placeId,
        }
      : undefined,
    district,
    department: department
      ? {
          ...department,
          placeId: selectedLevel === 'department' ? selectedPlaceId : department.placeId,
        }
      : undefined,
    region: region
      ? {
          ...region,
          placeId: selectedLevel === 'region' ? selectedPlaceId : region.placeId,
        }
      : undefined,
    country,
  };

  // Municipality (Da Nang, etc.): admin1 == city name → keep city, drop fake region.
  if (hierarchy.city?.name && hierarchy.region?.name && namesEqual(hierarchy.city.name, hierarchy.region.name)) {
    hierarchy.region = undefined;
  }

  // Province/municipality selected as city (VN admin1): promote admin1 → city.
  if (selectedLevel === 'city' && selectedPlaceId && !hierarchy.city) {
    const municipalityName =
      getComponent(components, 'locality')?.long_name ||
      getComponent(components, 'administrative_area_level_1')?.long_name ||
      getComponent(components, 'political')?.long_name ||
      '';
    hierarchy.city = {
      name: municipalityName,
      placeId: selectedPlaceId,
    };
  }
  // VN: admin_level_1 is the city/province — promote when locality missing
  // (street address in Đà Nẵng, or selecting Đà Nẵng itself).
  if (!hierarchy.city?.name && hierarchy.region?.name && hierarchy.country?.code === 'VN') {
    hierarchy.city = {
      name: hierarchy.region.name,
      placeId: selectedPlaceId || hierarchy.region.placeId,
      code: hierarchy.region.code,
    };
    hierarchy.region = undefined;
  }
  if (
    selectedLevel === 'city' &&
    hierarchy.city?.name &&
    hierarchy.region?.name &&
    namesEqual(hierarchy.city.name, hierarchy.region.name)
  ) {
    hierarchy.region = undefined;
  }

  if (selectedLevel === 'region' && selectedPlaceId) {
    hierarchy.region = {
      name: hierarchy.region?.name || getComponent(components, 'administrative_area_level_1')?.long_name || '',
      placeId: selectedPlaceId,
      code: hierarchy.region?.code,
    };
  }

  if (selectedLevel === 'country' && selectedPlaceId) {
    hierarchy.country = {
      name:
        country?.name ||
        getComponent(components, 'country')?.long_name ||
        getComponent(components, 'political')?.long_name ||
        '',
      code: country?.code || getComponent(components, 'country')?.short_name,
      placeId: selectedPlaceId,
    };
  }

  // Never keep country name as "region" (autocomplete path leak).
  if (hierarchy.region?.name && hierarchy.country?.name && namesEqual(hierarchy.region.name, hierarchy.country.name)) {
    hierarchy.region = undefined;
  }

  return hierarchy;
}
